import { createTheme } from "@mui/material";

declare module "@mui/material/styles" {
  interface Palette {
    black: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    gray: {
      light: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    black?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    gray?: {
      light: string;
      contrastText: string;
    };
    brand?: {
      blue: {
        main: string;
        border: string;
      };
    };
  }
}

export const palette = {
  primary: {
    '100': "#E6EFFC",
    main: "#0E6FFF",
    light: "#F3F8FF",
    white: "#FFFFFF",
    black: "#000000E5",
    gray: "#6B6B6B",
  },
  background: {
    light: "#F5F7FA",
  },
  secondary: {
    main: "#AAAAAA",
    light: "#F4F4F4",
    dark: "#555555",
    border: "#0b162014",
  },
  black: {
    main: "#2B2B2B",
    light: "#2B2B2B",
    dark: "#111111",
    contrastText: "#2B2B2B",
  },
  gray: {
    light: "#BFBFBF",
    contrastText: "#5c6b80",
  },
  success: {
    main: "#00C897",
  },
  error: {
    main: "#FF4D4D",
  }
};

const theme = createTheme({
  palette: {
    primary: palette.primary,
    secondary: palette.secondary,
    black: palette.black,
    gray: palette.gray,
    success: palette.success,
    error: palette.error,
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: `"Inter", sans-serif`,
          fontOpticalSizing: "auto",
          fontStyle: "normal",
          color: palette.black.main,
        },
        body1: {
          fontSize: "12px",
          lineHeight: "14px",
        },
        body2: {
          fontSize: "14px",
          lineHeight: "18px",
        },
        subtitle1: {
          color: palette.gray.contrastText,
          fontSize: "14px",
          fontWeight: 500,
        },
        h1: {
          fontSize: "28px",
          lineHeight: "32px",
          fontWeight: 600,
          letterSpacing: "-2%",
        },
        h2: {
          fontSize: "24px",
          lineHeight: "26px",
          fontWeight: 600,
          letterSpacing: "-2%",
        },
        h3: {
          fontSize: "20px",
          lineHeight: "22px",
          fontWeight: 500,
        },
        h4: {
          fontSize: "13px",
          lineHeight: "15px",
          letterSpacing: "-2%",
          fontWeight: 600,
        },
        h5: {
          fontSize: "16px",
          lineHeight: "22px",
          letterSpacing: "-2%",
          fontWeight: 600,
        },
        h6: {
          fontSize: "12px",
          lineHeight: "16px",
          letterSpacing: "-2%",
          fontWeight: 600,
        },
        button: {
          fontSize: "14px",
          lineHeight: "16px",
          fontWeight: 600,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          span: {
            fontSize: "16px",
            fontWeight: 600,
            color: palette.black.main,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          height: "fit-content",
          padding: "12px 24px"
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation: {
          border: `1px solid ${palette.secondary.border}`,
          boxShadow: "none",
          padding: "20px",
        },
        elevation0: {
          border: "1px solid rgba(11, 22, 32, 0.08)",
          boxShadow: "rgba(14, 111, 255, 0.06) 0px 0px 20px, rgba(255, 255, 255, 0.5) 0px 1px 0px inset"
        },
        elevation1: {
          border: "none",
          boxShadow: "0px 2px 2px 0px #0000001A",
        },
        elevation2: {
          backgroundColor: "#ffffff59",
          border: "none",
          boxShadow: `
            0 0 #0000,
            0 0 #0000,
            0 0 #0000,
            0 0 #0000,
            0 2px 2px -1px rgb(0 0 0 / 0.1),
            0 2px 2px -2px rgb(0 0 0 / 0.1)
          `,
        }
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          backgroundColor: palette.secondary.light,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "4px !important",
          fontSize: "14px",
          textTransform: "unset",
          boxShadow: "unset !important",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "4px !important",
          padding: "0 !important",
          paddingRight: "20px",
          fontSize: "14px",
          lineHeight: "16px",
          '& .MuiFilledInput-root': {
            backgroundColor: 'red',
          }
        },
        input: {
          borderRadius: "4px !important",
          padding: "8px 16px !important",
        },
        adornedEnd: {
          "& input": {
            // paddingRight: "22px !important",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: "-5px",
          color: "#6B6B6B",
          fontSize: "14px",
          lineHeight: "16px",
          fontWeight: 400,
          "&.MuiInputLabel-shrink": {
            fontSize: "16px",
            top: "4px",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          "& > .MuiPaper-root": {
            padding: "0 !important",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          lineHeight: "16px",
          // @ts-ignore
          color: "black.main",
          backgroundColor: "#FFF",
          height: "36px",
          paddingRight: "20px !important",
          "& .MuiSelect-select": {
            lineHeight: '22px',
            minHeight: "unset",
          },
          '& .MuiSelect-icon': {

          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "& > .MuiPaper-root": {
            padding: "0 !important",
          },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          top: "8px",
          span: {
            borderTopWidth: "1.5px",
            borderColor: "#D5D5D5",
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          svg: {
            width: "16px",
            height: "16px",
            // fill: "#D5D5D5",
            text: {
              fill: "transparent",
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#2B2B2B",
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "28px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          right: "16px",
          marginRight: "12px",
          fontSize: "20px"
        },
        positionStart: {
          marginLeft: "12px",
        },
        root: {
          margin: 0,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.secondary.border,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: palette.secondary.border,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: 500,
          height: "fit-content",
          span: {
            padding: "4px 8px"
          },
          border: '1px solid inherit',
        },
        colorDefault: {
          color: palette.secondary.dark,
          border: `1px solid ${palette.secondary.main}`
        },
        colorError: {
          border: `1px solid ${palette.error.main}`
        },
        colorSuccess: {
          border: `1px solid ${palette.success.main}`
        },
        colorPrimary: {
          border: `1px solid ${palette.primary.main}`
        },
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '22px',
          lineHeight: '24px'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          // position: 'absolute',
          // top: '34px',
          // bottom: '-22px'
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          padding: '8px',
          maxHeight: '36px',
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          '-webkit-transform': "none",
          transform: 'none',
          borderRadius: '4px',
        },
      }
    },
  },
});

export default theme;
